<template>
  <div class="tag-input">
    <label v-if="label" class="input-label">{{ label }}</label>
    <div class="tags-container">
      <div v-for="(tag, index) in modelValue" :key="index" class="tag">
        <span class="tag-text">{{ tag }}</span>
        <button class="tag-remove" @click="removeTag(index)">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 3L9 9M3 9L9 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <input
        v-model="inputValue"
        type="text"
        class="tag-text-input"
        :placeholder="modelValue.length === 0 ? placeholder : ''"
        @keydown.enter.prevent="addTag"
        @keydown.backspace="handleBackspace"
        @blur="addTag"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: string[]
  label?: string
  placeholder?: string
}>(), {
  placeholder: 'Add item...',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const inputValue = ref('')

const addTag = () => {
  const value = inputValue.value.trim()
  if (value && !props.modelValue.includes(value)) {
    emit('update:modelValue', [...props.modelValue, value])
  }
  inputValue.value = ''
}

const removeTag = (index: number) => {
  const newTags = [...props.modelValue]
  newTags.splice(index, 1)
  emit('update:modelValue', newTags)
}

const handleBackspace = () => {
  if (inputValue.value === '' && props.modelValue.length > 0) {
    removeTag(props.modelValue.length - 1)
  }
}
</script>

<style scoped>
.tag-input {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.input-label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-primary);
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  min-height: 44px;
  background: var(--surface-input);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.tags-container:focus-within {
  border-color: var(--color-primary-500);
  box-shadow: var(--shadow-focus);
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  background: var(--color-primary-50);
  color: var(--color-primary-600);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}

.tag-text {
  line-height: 1;
}

.tag-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--color-primary-400);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.tag-remove:hover {
  color: var(--color-primary-600);
  background: var(--color-primary-100);
}

.tag-text-input {
  flex: 1;
  min-width: 100px;
  padding: var(--space-1) 0;
  font-size: var(--text-sm);
  background: transparent;
  border: none;
  color: var(--text-primary);
}

.tag-text-input:focus {
  outline: none;
}

.tag-text-input::placeholder {
  color: var(--text-placeholder);
}
</style>
